// ÁTTEKINTÉS — „Jegyzet" csempe: egy személyes, CSAK szöveges jegyzet (nincs rich text → nincs szanitálási felület,
// megjelenítés `whitespace-pre-wrap`-pel a textarea-ban). Tárolás: a `notes` preferencia-kulcs (`{ v: 1, text }`).
//
// AUTOMATIKUS MENTÉS: 1 s debounce, `If-Match` (OCC) mellett; a mentés OPTIMISTA (a cache azonnal a beírt szöveget
// tartja, hiba esetén visszaáll). SAJÁT mentés-gép, nem `useEntityMutation`: az autosave NÉMA művelet (a hook minden
// sikernél toastot dobna, másodpercenként), a státuszt a csempe maga jelzi. A mentések SOROSÍTOTTAK: egy futó mentés
// alatt gépelt újabb szöveg a befejezés UTÁN megy ki az új `version`-nel — így a két mentés nem ütközik önmagával.
//
// 409 (másik eszközön módosult): sáv a csempén — „Az enyém marad" (a friss verzióra írom a sajátomat) / „Betöltöm a
// másikat". Megszemélyesítés alatt a jegyzet csak olvasható (az Odin az írást tiltja).
import { NOTES_MAX_LENGTH } from '@atlas/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { PanelCard } from '@/components/shared/panel-card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  dashboardKeys,
  isEnvelope,
  withEnvelopeValue,
  type CachedResponse,
} from '@/features/dashboard/api';
import { api } from '@/lib/api';

/** A gépelés utáni késleltetés a mentésig. */
export const NOTES_SAVE_DELAY_MS = 1000;
/** E hossz fölött jelenik meg a karakter-számláló (a plafon 90%-a). */
const COUNTER_THRESHOLD = Math.floor(NOTES_MAX_LENGTH * 0.9);

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface Conflict {
  text: string;
  version: number;
}

function textOf(value: unknown): string {
  return typeof value === 'object' && value !== null && 'text' in value ? String(value.text) : '';
}

export function NotesCard(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = api.session.useQuery(['session'], {});
  const readOnly = session.data?.status === 200 && Boolean(session.data.body.impersonation);
  const q = api.preferences.notesGet.useQuery(
    dashboardKeys.notes,
    {},
    { queryKey: dashboardKeys.notes, refetchOnWindowFocus: false },
  );
  const envelope = q.data?.status === 200 ? q.data.body : null;

  const [text, setText] = useState('');
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [conflict, setConflict] = useState<Conflict | null>(null);

  // a konfliktus-állapot a `save` önhívásához (ne induljon újabb mentés, amíg a felhasználó nem döntött)
  const conflictRef = useRef<Conflict | null>(null);
  conflictRef.current = conflict;
  const textRef = useRef('');
  const versionRef = useRef(0);
  const lastSavedRef = useRef('');
  const initRef = useRef(false);
  const inFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // az ELSŐ betöltött érték a szerkesztőbe — a későbbi háttér-frissítés NEM írja felül a gépelést
  useEffect(() => {
    if (envelope === null || initRef.current) return;
    initRef.current = true;
    const initial = textOf(envelope.value);
    textRef.current = initial;
    lastSavedRef.current = initial;
    versionRef.current = envelope.version;
    setText(initial);
  }, [envelope]);

  const save = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) return; // a befejezés után újraindul, ha van mit menteni
    const next = textRef.current;
    if (next === lastSavedRef.current) return;
    inFlightRef.current = true;
    let succeeded = false;
    setStatus('saving');
    // optimista: a cache azonnal a beírt szöveget tartja
    await queryClient.cancelQueries({ queryKey: dashboardKeys.notes });
    const previous = queryClient.getQueryData<CachedResponse>(dashboardKeys.notes);
    queryClient.setQueryData<CachedResponse | undefined>(
      dashboardKeys.notes,
      withEnvelopeValue(previous, { v: 1, text: next }),
    );
    const rollback = (): void => {
      queryClient.setQueryData(dashboardKeys.notes, previous);
    };
    try {
      const res = await api.preferences.notesPut.mutation({
        body: { v: 1, text: next },
        headers: { 'if-match': String(versionRef.current) },
      });
      if (res.status === 200) {
        versionRef.current = res.body.version;
        lastSavedRef.current = next;
        succeeded = true;
        queryClient.setQueryData<CachedResponse>(dashboardKeys.notes, {
          status: 200,
          body: res.body,
          headers: res.headers,
        });
        setStatus('saved');
      } else if (res.status === 409 || res.status === 428) {
        rollback();
        const fresh = await api.preferences.notesGet.query();
        if (fresh.status === 200 && isEnvelope(fresh.body)) {
          setConflict({ text: textOf(fresh.body.value), version: fresh.body.version });
        }
        setStatus('error');
      } else {
        rollback();
        setStatus('error');
        toast.error(
          t(res.status === 403 ? 'dashboard.notes.forbidden' : 'dashboard.notes.saveError'),
        );
      }
    } catch {
      rollback();
      setStatus('error');
      toast.error(t('dashboard.notes.saveError'));
    } finally {
      inFlightRef.current = false;
    }
    // a mentés közben gépelt szöveg: azonnal a következő kör (az új `version`-nel) — CSAK sikeres mentés után, különben
    // a hibás kérés végtelen újrapróbálássá válna
    if (succeeded && textRef.current !== lastSavedRef.current) {
      void save();
    }
  }, [queryClient, t]);

  const schedule = useCallback(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (conflictRef.current === null) void save();
    }, NOTES_SAVE_DELAY_MS);
  }, [save]);

  // lap-elhagyáskor (pl. szerkesztő-mód) a függő mentés azonnal kimegy
  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        void save();
      }
    },
    [save],
  );

  const onChange = (value: string): void => {
    textRef.current = value;
    setText(value);
    setStatus('idle');
    schedule();
  };

  const keepMine = (): void => {
    if (conflict === null) return;
    versionRef.current = conflict.version;
    lastSavedRef.current = conflict.text;
    setConflict(null);
    conflictRef.current = null;
    void save();
  };
  const loadTheirs = (): void => {
    if (conflict === null) return;
    versionRef.current = conflict.version;
    lastSavedRef.current = conflict.text;
    textRef.current = conflict.text;
    setText(conflict.text);
    setConflict(null);
    conflictRef.current = null;
    setStatus('saved');
  };

  const showCounter = text.length >= COUNTER_THRESHOLD;
  return (
    <PanelCard
      capped
      title={t('dashboard.tiles.notes.title')}
      loading={q.isLoading}
      skeletonRows={3}
    >
      <div className="space-y-2 p-3">
        {conflict !== null && (
          <div
            role="alert"
            className="bg-warning-soft text-foreground flex flex-wrap items-center gap-2 rounded-lg p-3 text-sm"
          >
            <span className="min-w-0 flex-1">{t('dashboard.notes.conflict')}</span>
            <Button size="xs" variant="outline" onClick={keepMine}>
              {t('dashboard.notes.keepMine')}
            </Button>
            <Button size="xs" variant="outline" onClick={loadTheirs}>
              {t('dashboard.notes.loadTheirs')}
            </Button>
          </div>
        )}
        {readOnly ? (
          <p className="text-subtle py-6 text-center text-sm" data-testid="notes-unavailable">
            {t('dashboard.notes.unavailableImpersonated')}
          </p>
        ) : (
          <Textarea
            value={text}
            onChange={(e) => onChange(e.target.value)}
            maxLength={NOTES_MAX_LENGTH}
            rows={6}
            aria-label={t('dashboard.tiles.notes.title')}
            placeholder={t('dashboard.notes.placeholder')}
            className="min-h-32 resize-y whitespace-pre-wrap"
          />
        )}
        <div className="text-subtle flex items-center justify-between gap-2 text-xs">
          <span aria-live="polite" data-testid="notes-status">
            {status === 'saving' && t('dashboard.notes.saving')}
            {status === 'saved' && t('dashboard.notes.saved')}
            {status === 'error' && conflict === null && t('dashboard.notes.saveFailed')}
          </span>
          {showCounter && (
            <span className="tabular-nums" data-testid="notes-counter">
              {t('dashboard.notes.counter', { count: text.length, max: NOTES_MAX_LENGTH })}
            </span>
          )}
        </div>
      </div>
    </PanelCard>
  );
}
